import { useEffect, useRef, useState } from "react";

interface CountdownTimerProps {
    initialMinutes: number;
    initialSeconds?: number;
    onTimeout: () => void;
    className?: string;
}

export default function CountdownTimer({
    initialMinutes,
    initialSeconds = 0,
    onTimeout,
    className,
}: CountdownTimerProps) {
    const [timeLeft, setTimeLeft] = useState<number>(
        initialMinutes * 60 + initialSeconds
    );

    // Keep a stable ref to onTimeout so the interval doesn't need to be
    // recreated every time the parent re-renders (which would recreate the
    // callback). Calling onTimeout() from inside a setTimeLeft updater
    // triggers React's "Cannot update a component while rendering a different
    // component" warning, so we call it in the effect body via this ref.
    const onTimeoutRef = useRef(onTimeout);
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    useEffect(() => {
        if (timeLeft <= 0) {
            onTimeoutRef.current();
            return;
        }

        const interval = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timeLeft]);

    const formatTime = () => {
        const mins = Math.floor(timeLeft / 60);
        const secs = timeLeft % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    return (
        <div className={className}>
            <span className="font-mono text-sm font-medium">
                {formatTime()}
            </span>
        </div>
    );
}
